import { APPOINTMENTS_SCHEDULE_DAYS } from '@avanta/shared/constants/common';
import { type AvailableDates } from '@avanta/shared/types/appointments';
import { formatDateISO } from '@avanta/shared/utils/date';
import { useQuery } from '@tanstack/react-query';
import { addDays, startOfDay } from 'date-fns';
import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { QUERY_KEYS } from 'constants/common';
import { AppointmentsReservePayload } from 'pages/AppointmentsReserve/constants';
import { type AppointmentsReserveFormData } from 'pages/AppointmentsReserve/types';
import { AppointmentService } from 'services/appointments';
import { mergeAvailableDates } from '../utils';

export interface UseFetchPsychologistAvailabilityResult {
  availableDates: AvailableDates;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
}

export const useFetchPsychologistAvailability = (
  enabled = true
): UseFetchPsychologistAvailabilityResult => {
  const { control } = useFormContext<AppointmentsReserveFormData>();
  const [patient, psychologist, specialityId, centerId, treatmentId, contractId] = useWatch({
    control,
    name: [
      AppointmentsReservePayload.patient,
      AppointmentsReservePayload.psychologist,
      AppointmentsReservePayload.specialityBooked,
      AppointmentsReservePayload.backofficeCenterId,
      AppointmentsReservePayload.psychologistServiceBooked,
      AppointmentsReservePayload.contractId,
    ],
  });
  const patientId = patient?.id ?? '';
  const psychologistId = psychologist?.id ?? '';
  const today = useMemo(() => startOfDay(new Date()), []);
  const start = formatDateISO(today);
  const end = formatDateISO(addDays(today, APPOINTMENTS_SCHEDULE_DAYS));
  const isCalendarQueryEnabled =
    enabled &&
    !!patientId &&
    !!psychologistId &&
    !!specialityId &&
    !!centerId &&
    !!treatmentId &&
    !!contractId;

  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: [
      QUERY_KEYS.FETCH_PSYCHOLOGIST_AVAILABILITY,
      patientId,
      psychologistId,
      specialityId,
      centerId,
      treatmentId,
      contractId,
      start,
      end,
    ],
    queryFn: () =>
      AppointmentService.getPsychologistAvailability({
        patientId,
        psychologistId,
        specialityId: Number(specialityId),
        centerId,
        treatmentId: Number(treatmentId),
        start,
        end,
      }),
    select: response => response.data?.flatMap(agenda => agenda.dailySlots),
    enabled: isCalendarQueryEnabled,
  });

  const availableDates = useMemo(
    () => (isCalendarQueryEnabled && data ? mergeAvailableDates(data) : {}),
    [data, isCalendarQueryEnabled]
  );

  return { availableDates, isLoading, isFetching, isError };
};
